/**
 * Status Helper Functions
 *
 * Utilities for categorizing and styling ticket statuses
 */

import { TicketInternalStatus } from "shared/types/helpdesk";

/**
 * Status categories based on workflow phase
 */
export type StatusCategory = 'open' | 'completion' | 'closed';

/**
 * Open ticket statuses - actively being worked on
 */
export const OPEN_STATUSES: TicketInternalStatus[] = [
  TicketInternalStatus.NEW_UNANSWERED,
  TicketInternalStatus.CONFIRMATION_SENT,
  TicketInternalStatus.DAILY_MEETING_TOPICS,
  TicketInternalStatus.ADDITIONAL_INFO_NEEDED,
  TicketInternalStatus.SUPPLIER_TO_CONTACT,
  TicketInternalStatus.SUPPLIER_CONTACTED,
];

/**
 * Completion/Afronding statuses - final stages before closing
 */
export const COMPLETION_STATUSES: TicketInternalStatus[] = [
  TicketInternalStatus.EXECUTION_DATE_KNOWN,
  TicketInternalStatus.WORK_COMPLETED,
];

/**
 * Get the category of a status
 */
export function getStatusCategory(status: TicketInternalStatus): StatusCategory {
  if (OPEN_STATUSES.includes(status)) {
    return 'open';
  }
  if (COMPLETION_STATUSES.includes(status)) {
    return 'completion';
  }
  return 'closed';
}

/**
 * Get display colors for a status chip
 */
export function getStatusChipColors(status: TicketInternalStatus): {
  bg: string;
  text: string;
  border: string;
} {
  const category = getStatusCategory(status);

  switch (category) {
    case 'open':
      return {
        bg: 'bg-brick-primary/10',
        text: 'text-brick-primary',
        border: 'border-brick-primary/20',
      };
    case 'completion':
      return {
        bg: 'bg-moss-primary/10',
        text: 'text-moss-primary',
        border: 'border-moss-primary/20',
      };
    case 'closed':
      return {
        bg: 'bg-neutral-200',
        text: 'text-neutral-600',
        border: 'border-neutral-300',
      };
  }
}

/**
 * Get a short label for the status (removes parenthetical notes)
 */
export function getStatusShortLabel(status: TicketInternalStatus): string {
  const fullLabel = status as string;

  // Remove parenthetical content for cleaner display
  // "Nieuwe tickets (Onbeantwoord)" -> "Nieuwe tickets"
  return fullLabel.replace(/\s*\([^)]*\)/g, '').trim();
}

/**
 * Get a category label for display
 */
export function getCategoryLabel(category: StatusCategory): string {
  switch (category) {
    case 'open':
      return 'Open';
    case 'completion':
      return 'In afronding';
    case 'closed':
      return 'Voltooid';
  }
}
